import { DateTime } from 'luxon';
import api from '@/components/mixins/baseApi';

/**
 * Избранное деловой программы: секции и доклады.
 *
 * Договорённость с бэком описана в docs/favorite-program-api.md:
 * `POST /section/{id}/favorite` и `POST /lecture/{id}/favorite` добавляют,
 * `DELETE` убирает, а список отметок отдаёт `GET /program/favorite` — по дням,
 * причём в дне лежат только ссылки `{ id, type }`, без полей карточки.
 *
 * Маршруты приняты 06.09.2026: `node tools/check-favorite-program-api.mjs
 * --exh 8 --app-id 909950` — 20 проверок из 20. Флаг остаётся на случай, если
 * маршруты на клиентском стенде окажутся старше: дёргать их вслепую нельзя,
 * 404 в baseApi.js уводит человека на страницу «не найдено» прямо с экрана
 * программы.
 *
 * Локальное хранение не отменяется и после включения флага:
 *   — гость отмечает только у себя в браузере. Его запрос получил бы 401, а тот
 *     в baseApi.js стирает токен и уводит на экран входа;
 *   — авторизованному локальная копия служит кешем отрисовки: календарь
 *     показывается сразу, а обновляется, когда ответят сервер и дни программы.
 *
 * Выключать флаг обратно — значит вернуть прежнее поведение целиком: отметки
 * снова живут только в браузере, экранный код при этом не меняется.
 */
export const PROGRAM_FAVORITE_API_READY = true;

/**
 * Ключ календаря. Прежний, из methodsCalendar: в нём лежат целые карточки, а не
 * идентификаторы. Не меняем — иначе у людей пропало бы накопленное, а гостю
 * карточки собирать не из чего.
 */
export function favoriteCalendarKey(exhId) {
  return `exh-${exhId}-calendar`;
}

/**
 * Очередь операций, которые не доехали до сервера. Наполняется при ошибке сети
 * и при первом входе — накопленным, пока человек ходил гостем.
 */
export function favoriteQueueKey(exhId) {
  return `exh-${exhId}-favorite-program-queue`;
}

function readList(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return [];
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

export function readFavoriteCalendar(exhId) {
  return readList(favoriteCalendarKey(exhId));
}

export function writeFavoriteCalendar(exhId, cards) {
  localStorage.setItem(favoriteCalendarKey(exhId), JSON.stringify(cards));
}

/**
 * Разделители дат в календаре — тоже элементы списка (`type: 'date'`), их
 * добавляет sortCalendar и сохраняет вместе с остальными. На сервер уходят
 * только настоящие элементы программы.
 */
export function favoriteItemsOnly(list) {
  return list.filter((item) => item.type === 'section' || item.type === 'lecture');
}

export function readFavoriteQueue(exhId) {
  return readList(favoriteQueueKey(exhId));
}

export function writeFavoriteQueue(exhId, queue) {
  localStorage.setItem(favoriteQueueKey(exhId), JSON.stringify(queue));
}

/**
 * Ставит операцию в очередь. Прежняя операция по тому же элементу вытесняется:
 * важно последнее решение человека, а не путь, которым он к нему пришёл.
 * Сравниваем по id — нумерация элементов программы сквозная, секции и доклады
 * не пересекаются.
 */
export function queueFavoriteOperation(exhId, item, action) {
  const queue = readFavoriteQueue(exhId).filter((op) => op.id !== item.id);
  queue.push({ id: item.id, type: item.type, action });
  writeFavoriteQueue(exhId, queue);
}

/** Выход стирает и календарь, и очередь: см. комментарий в methodsLogin.js. */
export function clearProgramFavorite(exhId) {
  localStorage.removeItem(favoriteCalendarKey(exhId));
  localStorage.removeItem(favoriteQueueKey(exhId));
}

// Заголовок секции склеивается из name и title с точками — ровно так же, как в
// SectionCard.sectionTitle. Карточка, собранная из ответа сервера, должна
// выглядеть неотличимо от собранной на экране.
function sectionTitleOf(section) {
  const name = section.name ? section.name : '';
  const title = section.title ? section.title : '';
  const dotName = name && name[name.length - 1] !== '.' ? '.' : '';
  const dotTitle = title && title[title.length - 1] !== '.' ? '.' : '';

  return `${name}${dotName} ${title}${dotTitle}`;
}

/**
 * Карточка секции для календаря — тот же набор полей, что отдаёт
 * SectionCard.dataForMarkFavorit.
 *
 * Одно отличие: там спикеры прячутся у раскрытой секции (`isSpeakers` смотрит на
 * состояние экрана), здесь прятать не от чего — карточка собирается из данных.
 */
export function sectionToCard(section) {
  return {
    id: section.id,
    hashLink: `s_${section.id}`,
    start_ts: section.start_ts,
    duration_ts: section.duration_ts,
    place: section.place,
    main_title: sectionTitleOf(section),
    sub_title: null,
    moderators: section.moderators || [],
    speakers: section.speakers || [],
    type: 'section',
  };
}

/**
 * Карточка доклада. Место, заголовок секции и модераторов доклад берёт у
 * родителя — как SectionCard.markLectureFavorit при нажатии закладки.
 */
export function lectureToCard(lecture, section) {
  return {
    id: lecture.id,
    hashLink: `s_${section.id}_l_${lecture.id}`,
    start_ts: lecture.start_ts,
    duration_ts: lecture.duration_ts,
    place: section.place,
    main_title: sectionTitleOf(section),
    sub_title: lecture.title,
    moderators: section.moderators || [],
    speakers: lecture.speakers || [],
    type: 'lecture',
  };
}

/**
 * Один заход на всё приложение. Старт и экран просыпаются почти одновременно —
 * без этого сервер получал бы два одинаковых круга запросов подряд, а за ними
 * ещё по разу каждый день программы. Состояние общее на модуль: у каждого
 * экрана свой экземпляр миксина, а сверять состав нужно один раз на всех.
 */
const SYNC_FRESH_MS = 10000;
let syncInFlight = null;
let syncFreshUntil = 0;
let syncResult = null;

export default {
  mixins: [api],

  methods: {
    /**
     * Уходить запросам или нет. Гость и выключенный флаг — это два разных
     * случая с одним ответом: работаем на локальном хранилище.
     */
    programFavoriteEnabled() {
      return PROGRAM_FAVORITE_API_READY && this.$store.getters.isAuth;
    },

    /**
     * Ставит или снимает отметку. Возвращает промис с итоговым состоянием.
     * Не прошедшая операция остаётся в очереди и уедет при следующей попытке —
     * при старте приложения или при открытии программы и календаря.
     */
    setProgramFavorite(item, state) {
      if (!this.programFavoriteEnabled()) return Promise.resolve(state);

      const exhId = this.$store.getters.getExhId;

      return this.baseAxios({
        method: state ? 'post' : 'delete',
        url: `${this.$store.getters.apiBasePath}/${item.type}/${item.id}/favorite`,
      })
        .then((data) => {
          const confirmed = data && data.results ? data.results.is_favorite : null;
          return typeof confirmed === 'boolean' ? confirmed : state;
        })
        .catch((error) => {
          // Обрыв связи — не отказ сервера: решение человека верное, доехать не
          // смогло. Такую операцию держим в очереди и на экране не откатываем.
          // Отказ сервера, наоборот, откатывается: там мы разошлись с истиной.
          if (error && error.code === 'ERR_NETWORK') {
            queueFavoriteOperation(exhId, item, state ? 'add' : 'remove');
          }
          return Promise.reject(error);
        });
    },

    /** Список отметок днями. Гостю и при выключенном флаге — null, а не пусто. */
    getProgramFavorite() {
      if (!this.programFavoriteEnabled()) return Promise.resolve(null);

      return this.baseAxios({
        method: 'get',
        url: `${this.$store.getters.apiProgramsPath}/favorite`,
      })
        .then((data) => (data && Array.isArray(data.results) ? data.results : []))
        .catch((error) => Promise.reject(error));
    },

    /**
     * Досылает накопленное. Операции уходят по одной и по порядку: две подряд по
     * одному элементу должны лечь в том же порядке, в каком их нажимал человек.
     * Очередь очищается заранее — упавшие вернёт в неё setProgramFavorite.
     */
    flushProgramFavoriteQueue() {
      if (!this.programFavoriteEnabled()) return Promise.resolve(false);

      const exhId = this.$store.getters.getExhId;
      const queue = readFavoriteQueue(exhId);
      if (!queue.length) return Promise.resolve(false);

      writeFavoriteQueue(exhId, []);

      return queue.reduce(
        (chain, op) => chain.then(() => this.setProgramFavorite(op, op.action === 'add')
          .catch(() => false)),
        Promise.resolve(),
      ).then(() => true);
    },

    /**
     * Первый вход: всё, что человек отметил гостем, становится очередью на
     * отправку. Локальное и серверное складываются, а не замещают друг друга.
     */
    enqueueLocalProgramFavorite() {
      const exhId = this.$store.getters.getExhId;

      favoriteItemsOnly(readFavoriteCalendar(exhId))
        .forEach((item) => queueFavoriteOperation(exhId, item, 'add'));
    },

    /**
     * Передача накопленного гостем при входе: всё локальное становится очередью
     * и уезжает на сервер фоном. Вход этого не ждёт — человек идёт дальше, а не
     * прошедшее повторится при следующей попытке.
     */
    handoffProgramFavorite() {
      if (!this.programFavoriteEnabled()) return Promise.resolve(false);

      this.enqueueLocalProgramFavorite();

      return this.flushProgramFavoriteQueue();
    },

    /** Ключ дня программы: yyyyLLdd в часовом поясе мероприятия. */
    programDayKey(ts) {
      return DateTime.fromMillis(ts)
        .setZone(this.$store.getters.getTime_UTC)
        .toFormat('yyyyLLdd');
    },

    /**
     * День программы целиком. Кеш, который пишет getProgramList, служит ответом
     * при обрыве связи: календарь лучше показать вчерашним, чем не показать.
     */
    fetchProgramDay(day) {
      const exhId = this.$store.getters.getExhId;
      const cached = () => {
        const raw = localStorage.getItem(`exh-${exhId}-bp-${day}`);
        if (!raw || raw === 'null') return [];
        try {
          const data = JSON.parse(raw);
          return Array.isArray(data.results) ? data.results : [];
        } catch (e) {
          return [];
        }
      };

      return this.baseAxios({
        method: 'get',
        url: `${this.$store.getters.apiProgramsPath}/${day}`,
      })
        .then((data) => {
          if (data) localStorage.setItem(`exh-${exhId}-bp-${day}`, JSON.stringify(data));
          return data && Array.isArray(data.results) ? data.results : cached();
        })
        .catch(() => cached());
    },

    /**
     * Собирает карточки по ссылкам из ответа сервера. Доклад ищется внутри
     * секций: отдельного маршрута у доклада нет, и часть полей всё равно берётся
     * у родителя.
     */
    collectFavoriteCards(groups, sections) {
      const cards = [];

      groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          if (item.type === 'section') {
            const section = sections.find((it) => it.id === item.id);
            if (section) cards.push(sectionToCard(section));
            return;
          }

          const parent = sections
            .find((it) => (it.lectures || []).some((lecture) => lecture.id === item.id));
          if (!parent) return;

          const lecture = parent.lectures.find((it) => it.id === item.id);
          cards.push(lectureToCard(lecture, parent));
        });
      });

      return cards;
    },

    /**
     * Полная синхронизация: досылаем накопленное, забираем список, добираем дни,
     * собираем карточки. Возвращает карточки или null, если запросы не уходили
     * (гость, выключенный флаг). Хранилище не трогает — этим занят methodsCalendar.
     */
    syncProgramFavorite() {
      if (!this.programFavoriteEnabled()) return Promise.resolve(null);
      if (syncInFlight) return syncInFlight;
      if (Date.now() < syncFreshUntil) return Promise.resolve(syncResult);

      syncInFlight = this.flushProgramFavoriteQueue()
        .then(() => this.getProgramFavorite())
        .then((groups) => {
          if (!groups) return null;
          if (!groups.length) return [];

          const days = [];
          groups.forEach((group) => {
            const day = this.programDayKey(group.start_ts);
            if (!days.includes(day)) days.push(day);
          });

          // Дни забираем по одному: их столько, сколько дней в избранном, и
          // веером они бьют по сети ради экрана, который уже нарисован кешем.
          return days.reduce(
            (chain, day) => chain.then((acc) => this.fetchProgramDay(day)
              .then((sections) => [...acc, ...sections])),
            Promise.resolve([]),
          ).then((sections) => this.collectFavoriteCards(groups, sections));
        })
        .then((cards) => {
          syncResult = cards;
          syncFreshUntil = Date.now() + SYNC_FRESH_MS;
          return cards;
        })
        .finally(() => {
          syncInFlight = null;
        });

      return syncInFlight;
    },
  },
};
